/**
 * The till's own stock figures, per counter.
 *
 * Kept per section **and** product. It used to be one row per product, which
 * was fine for a till that sells for one counter and wrong for one that sells
 * for two: the catalogue pull brought a figure for each section and the second
 * overwrote the first, so the grid showed whichever counter happened to arrive
 * last. Every read and write of local stock goes through here, so that rule
 * lives in one place.
 *
 * The figure shown is `serverQty + pendingDelta`: the last balance the server
 * sent, plus this till's own movements it has not yet acknowledged.
 */

import { db } from "@/db/database";

export const localStockRepository = {
  /**
   * What is on hand, per product, as the grid shows it.
   *
   * For one counter when a section is given. With none, the total across the
   * counters this till knows about — what the server's own grid shows a
   * cashier who has not chosen a counter.
   */
  async onHand(productIds: string[], sectionId: string | null): Promise<Map<string, number>> {
    const wanted = new Set(productIds);
    const rows = sectionId
      ? await db.stockLevels.where("sectionId").equals(sectionId).toArray()
      : await db.stockLevels.where("productId").anyOf(productIds).toArray();

    const totals = new Map<string, number>();
    for (const row of rows) {
      if (!wanted.has(row.productId)) continue;
      const figure = Number(row.serverQty) + Number(row.pendingDelta);
      totals.set(row.productId, (totals.get(row.productId) ?? 0) + figure);
    }
    return totals;
  },

  /**
   * Move this till's own figure for a product: negative for a sale, positive
   * for goods back on the shelf. Written to `pendingDelta`, which a pull leaves
   * alone until the server's balance already includes it.
   *
   * The section is the counter the server will move — the till's own. When the
   * till does not know it, a product held at exactly one counter is still
   * unambiguous; otherwise nothing is guessed and the next pull corrects it.
   */
  async move(sectionId: string | null, productId: string, delta: number): Promise<void> {
    let row = sectionId ? await db.stockLevels.get([sectionId, productId]) : undefined;
    if (!row && !sectionId) {
      const candidates = await db.stockLevels.where("productId").equals(productId).toArray();
      if (candidates.length === 1) row = candidates[0];
    }
    if (!row) return;

    await db.stockLevels.put({
      ...row,
      pendingDelta: (Number(row.pendingDelta) + delta).toFixed(3),
    });
  },

  /** The server's balance for one counter, keeping this till's unsent movements. */
  async receive(sectionId: string, productId: string, serverQty: string): Promise<void> {
    const existing = await db.stockLevels.get([sectionId, productId]);
    await db.stockLevels.put({
      productId,
      sectionId,
      serverQty,
      pendingDelta: existing?.pendingDelta ?? "0",
      reorderLevel: existing?.reorderLevel ?? "0",
      syncedAt: new Date().toISOString(),
    });
  },
};
